import React, { Fragment, useEffect, useState } from 'react'
import classes from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../../contexts/kato.context';
import FullscreenClose from './fullscreen-close.component';
import { useDebounce } from '../../../../hooks/useDebounce.hook';
import { IRoad, IRoadShareStats, ITotalRoadShareStats } from '../../../../types/kato.types';
import { downloadRoadToExcel, searchRoad } from '../../../../requests/public-road.request';
import { useShared } from '../../../../contexts/shared.context';
import { SortIcon } from '../../../../assets/icons';

const translationKey = 'cards.road-card';
const baseClass = 'road-fullscreen';

type HeaderConfig = {
    title: string | number;
    colSpan?: number;
    rowSpan?: number;
    sortable?: boolean;
    key?: string;
};

type HeaderGroup = HeaderConfig[];

const headerKeys: (keyof Omit<IRoadShareStats, 'roadsList'>)[] = [
    'totalKm', 'totalPrevYearGoodConditionKm', 'totalPrevYearGoodConditionPercentage', 'totalPrevYearSatisfactoryConditionKm',
    'totalPrevYearSatisfactoryConditionPercentage', 'totalPrevYearPoorConditionKm', 'totalPrevYearPoorConditionPercentage',
    'totalCurYearGoodConditionKm', 'totalCurYearGoodConditionPercentage', 'totalCurYearSatisfactoryConditionKm',
    'totalCurYearSatisfactoryConditionPercentage', 'totalCurYearPoorConditionKm', 'totalCurYearPoorConditionPercentage',
]

const bodyKeys: (keyof IRoad)[] = [
    'index', 'roadName', 'totalKm', 'prevYearGoodConditionKm', 'prevYearGoodConditionPercentage',
    'prevYearSatisfactoryConditionKm', 'prevYearSatisfactoryConditionPercentage', 'prevYearPoorConditionKm',
    'prevYearPoorConditionPercentage', 'curYearGoodConditionKm', 'curYearGoodConditionPercentage',
    'curYearSatisfactoryConditionKm', 'curYearSatisfactoryConditionPercentage', 'curYearPoorConditionKm',
    'curYearPoorConditionPercentage', 'ongoingProjects'
]

const RoadFullscreen = () => {
    const { t } = useTranslation();
    const { totalRoadShareStats } = useKato().katoGeneralInfo;
    const [query, setQuery] = useState<string>('');
    const [filteredData, setFilteredData] = useState<ITotalRoadShareStats | null>();
    const { selectedInfoYear } = useShared();
    const debouncedQuery = useDebounce(query, 500);
    const [sortKey, setSortKey] = useState<keyof IRoad | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const handleSort = (key: keyof IRoad) => {
        if (!filteredData?.roadShareStats?.length) return;

        const newDirection = (sortKey === key && sortDirection === 'asc') ? 'desc' : 'asc';
        setSortKey(key);
        setSortDirection(newDirection);

        const sortedStats = filteredData.roadShareStats.map((item) => {
            const sortedRoads = [...item.roadsList].sort((a, b) => {
                const aVal = a[key] ?? '';
                const bVal = b[key] ?? '';

                let result: number;

                if (key === 'index') {
                    const aIndex = +aVal.toString().split('-').pop()!;
                    const bIndex = +bVal.toString().split('-').pop()!;
                    result = aIndex - bIndex;
                } else if (typeof aVal === 'string' || typeof bVal === 'string') {
                    result = aVal.toString().localeCompare(bVal.toString());
                } else {
                    result = Number(aVal) - Number(bVal);
                }

                return newDirection === 'asc' ? result : -result;
            });

            return { ...item, roadsList: sortedRoads };
        });

        setFilteredData({ ...filteredData, roadShareStats: sortedStats });
    };


    const handleDownload = () => {
        downloadRoadToExcel(query)
    }

    useEffect(() => {
        if (debouncedQuery.trim().length > 0) {
            searchRoad(debouncedQuery).then((res) => setFilteredData(res.data));
        } else {
            setFilteredData(totalRoadShareStats);
        }
    }, [debouncedQuery, totalRoadShareStats]);

    const renderCellValue = (road: IRoad, key: keyof IRoad) => {
        const val = road[key];
        if (val === null || val === undefined || val === '') return '-';

        if (['roadName', 'ongoingProjects', 'index'].includes(key)) return val;
        return isNaN(+val) ? val : (+val).toFixed(1);
    };

    const generateHeaders = (
        t: (key: string, options?: any) => string,
        selectedInfoYear: number,
        filteredData?: ITotalRoadShareStats
    ) => {
        const percentColumns = ['good', 'satisfactory', 'not-satisfactory'];
        const columnTypes = ['km', '%'];

        const headers: HeaderGroup[] = [
            [
                { title: t(`${translationKey}.table.number`), rowSpan: 3 },
                { key: 'index', title: t(`${translationKey}.table.index`), rowSpan: 3, sortable: true },
                { key: 'roadName', title: t(`${translationKey}.table.roadName`), rowSpan: 3, sortable: true },
                { key: 'totalKm', title: t(`${translationKey}.table.totalKm`), rowSpan: 3, sortable: true },
                { title: t(`${translationKey}.table.prevYear-result`, { year: selectedInfoYear - 1 }), colSpan: 6 },
                { title: t(`${translationKey}.table.curYear-result`, { year: selectedInfoYear }), colSpan: 6 },
                { key: 'ongoingProjects', title: t(`${translationKey}.table.ongoingProjects`), rowSpan: 5, sortable: true }
            ],
            [
                ...percentColumns.map(item => ({
                    title: t(`${translationKey}.table.${item}`),
                    colSpan: 2
                })),
                ...percentColumns.map(item => ({
                    title: t(`${translationKey}.table.${item}`),
                    colSpan: 2
                }))
            ],
            [
                ...Array(6).fill(0).flatMap(() =>
                    columnTypes.map(type => ({
                        title: t(`${translationKey}.table.${type}`)
                    }))
                )
            ],
            [
                { title: t(`${translationKey}.table.result`), colSpan: 3 },
                ...headerKeys.map(key => ({
                    title: filteredData?.[key as keyof ITotalRoadShareStats] as string | number ?? 0
                }))
            ],
            [
                { title: '' },
                { title: t(`${translationKey}.table.satisfactory-percent`), colSpan: 3 },
                {
                    title: filteredData?.prevYearTotalFineRoadPercentage || 0,
                    colSpan: 6,
                },
                {
                    title: filteredData?.curYearTotalFineRoadPercentage || 0,
                    colSpan: 6,
                },
            ]
        ];

        return <thead>
            {headers.map((row, rowIndex) => (
                <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                        <th
                            key={cellIndex}
                            colSpan={cell.colSpan || 1}
                            rowSpan={cell.rowSpan || 1}
                        >
                            <div className={classes[`${baseClass}__header`]}>
                                {cell.title}
                                {cell.sortable && cell.key && <SortIcon onClick={() => handleSort(cell.key as keyof IRoad)} />}
                            </div>
                        </th>
                    ))}
                </tr>
            ))}
        </thead>
    };

    const generateBody = () => {
        if (!filteredData) return null;

        return <tbody>
            {filteredData.roadShareStats.map((item, rowIndex) => (
                <Fragment key={rowIndex}>
                    <tr className={classes[`${baseClass}__full-row`]}>
                        <td>
                            <span>{rowIndex + 1}</span>
                        </td>
                        <td colSpan={16}>
                            <span>{item.name}</span>
                        </td>
                    </tr>

                    {
                        item.roadsList.map((road, roadIndex) => (
                            <tr className={classes[`${baseClass}__cells`]} key={roadIndex}>
                                <td>
                                    <span>{roadIndex + 1}</span>
                                </td>
                                {bodyKeys.map((key, keyIndex) => (
                                    <td key={keyIndex}><span>{renderCellValue(road, key)}</span></td>
                                ))}
                            </tr>
                        ))
                    }

                    <tr className={classes[`${baseClass}__cells`]}>
                        <td colSpan={3}>
                            <span>{t('total')}</span>
                        </td>
                        {headerKeys.map((key, keyIndex) => (
                            <td key={keyIndex}>
                                <span>{item[key] ?? '-'}</span>
                            </td>
                        ))}
                        <td>
                            <span>
                                -
                            </span>
                        </td>
                    </tr>

                    <tr className={classes[`${baseClass}__cells`]}>
                        <td colSpan={4}>
                            <span>{t(`${translationKey}.road-share`)}</span>
                        </td>
                        <td colSpan={6}>
                            {item.totalPrevYearSatisfactoryConditionPercentage}
                        </td>
                        <td colSpan={6}>
                            {item.totalCurYearSatisfactoryConditionPercentage}
                        </td>
                        <td colSpan={6}>
                            -
                        </td>
                    </tr>
                </Fragment>
            ))}
        </tbody>
    }

    if (!totalRoadShareStats || !filteredData) return null;
    return (
        <div className={classes[baseClass]}>
            <FullscreenClose />
            <div className={classes[`${baseClass}__filters`]}>
                <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder='Поиск...' />
            </div>
            <div className={classes[`${baseClass}__table`]}>
                <table>
                    {generateHeaders(t, selectedInfoYear, filteredData)}
                    {generateBody()}
                </table>
            </div>

        </div>
    )
}

export default RoadFullscreen